import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Ticket, Loader2, Copy, Gift, Users, IndianRupee, PiggyBank, CalendarClock, ShieldCheck, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SEOHead from "@/components/SEOHead";

const STATUS_STYLES = {
  active: "bg-green-50 text-green-700 border-green-200",
  used: "bg-gray-100 text-gray-500 border-gray-200",
  expired: "bg-red-50 text-red-600 border-red-200",
};

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1DA] rounded-lg px-4 py-3.5 text-center">
      <Icon size={17} className="mx-auto mb-1.5 text-[#b1976b]" />
      <p className="font-heading font-bold text-xl text-[#1A1A1A] leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-[#54595f] mt-1.5 font-semibold">{label}</p>
    </div>
  );
}

export default function MyCoupons() {
  const [whatsapp, setWhatsapp] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [coupons, setCoupons] = useState(null);
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const sendOtp = async (e) => {
    e?.preventDefault();
    const digits = whatsapp.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Enter a valid WhatsApp number");
      return;
    }
    setSending(true);
    try {
      await api.post("/referral/my-coupons/send-otp", { whatsapp });
      setOtpSent(true);
      setResendIn(60);
      toast.success("Verification code sent to your WhatsApp");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send the code");
    } finally {
      setSending(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (otp.replace(/\D/g, "").length !== 6) {
      toast.error("Enter the 6-digit code from WhatsApp");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post("/referral/my-coupons/verify", { whatsapp, otp });
      setCoupons(data.coupons);
      setStats(data.stats);
      localStorage.setItem("gu_customer_wa", whatsapp);
      toast.success("Verified — here are your coupons");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const reset = () => {
    setCoupons(null);
    setStats(null);
    setOtp("");
    setOtpSent(false);
    setResendIn(0);
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="min-h-[70vh] bg-[#FAF8F4] py-12 px-4">
      <SEOHead pageKey="my-coupons" />
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#b1976b]/15 mb-3"><Gift size={26} className="text-[#b1976b]" /></div>
          <h1 className="font-heading font-bold text-3xl text-[#1A1A1A]">My Reward Coupons</h1>
          <p className="text-sm text-[#54595f] mt-2">Enter your WhatsApp number and we'll send a one-time code to verify it's you — then see your full referral dashboard.</p>
        </div>

        {coupons === null && (
          <div className="bg-white border border-[#E5E1DA] rounded-lg p-5 mb-6" data-testid="my-coupons-otp-card">
            <form onSubmit={otpSent ? verify : sendOtp} className="space-y-3">
              <div className="flex gap-2">
                <Input data-testid="my-coupons-whatsapp-input" placeholder="Your WhatsApp number (e.g. 9876543210)" value={whatsapp}
                  onChange={(e) => { setWhatsapp(e.target.value); if (otpSent) { setOtpSent(false); setOtp(""); } }}
                  className="h-12" disabled={sending || verifying} />
                {!otpSent && (
                  <Button type="submit" data-testid="my-coupons-send-otp-btn" disabled={sending}
                    className="h-12 px-5 bg-[#1A1A1A] hover:bg-[#333] text-white font-bold shrink-0">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <><MessageCircle size={15} className="mr-1.5" /> Send Code</>}
                  </Button>
                )}
              </div>
              {otpSent && (
                <div data-testid="my-coupons-otp-step">
                  <p className="text-[12px] text-[#54595f] mb-2 flex items-center gap-1.5">
                    <ShieldCheck size={13} className="text-green-600" /> We sent a 6-digit code to your WhatsApp. Enter it below.
                  </p>
                  <div className="flex gap-2">
                    <Input data-testid="my-coupons-otp-input" placeholder="6-digit code" inputMode="numeric" maxLength={6} value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} className="h-12 w-40 font-mono tracking-[0.3em] text-center text-lg" />
                    <Button type="submit" data-testid="my-coupons-verify-btn" disabled={verifying || otp.length !== 6}
                      className="h-12 px-6 bg-[#b1976b] hover:bg-[#9E855A] text-white font-bold">
                      {verifying ? <Loader2 size={16} className="animate-spin" /> : "Verify & View"}
                    </Button>
                  </div>
                  <button type="button" data-testid="my-coupons-resend-btn" onClick={sendOtp} disabled={resendIn > 0 || sending}
                    className="mt-2 text-[12px] font-semibold text-[#b1976b] underline disabled:text-[#54595f]/50 disabled:no-underline">
                    {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                  </button>
                </div>
              )}
            </form>
          </div>
        )}

        {stats && (stats.friends_referred > 0 || stats.coupons_earned > 0) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-6" data-testid="my-coupons-stats">
            <StatCard icon={Users} label="Friends Referred" value={stats.friends_referred} testid="my-coupons-stat-friends" />
            <StatCard icon={Ticket} label="Coupons Earned" value={stats.coupons_earned} testid="my-coupons-stat-coupons" />
            <StatCard icon={IndianRupee} label="Total Earned" value={`₹${Number(stats.total_earned_inr).toLocaleString("en-IN")}`} testid="my-coupons-stat-earned" />
            <StatCard icon={PiggyBank} label="Total Saved" value={`₹${Number(stats.total_saved_inr).toLocaleString("en-IN")}`} testid="my-coupons-stat-saved" />
          </div>
        )}
        {coupons !== null && (
          <>
            {coupons.length === 0 ? (
              <p data-testid="my-coupons-empty" className="text-center text-sm text-[#54595f] bg-white border border-[#E5E1DA] rounded-lg py-8 px-4">
                No coupons found for this number yet. Complete an application, then share your referral link from the tracking page to start earning coupons!{" "}
                <Link to="/refer-and-earn" className="text-[#b1976b] underline font-semibold">How it works →</Link>
              </p>
            ) : (
              <div className="space-y-2.5" data-testid="my-coupons-list">
                {coupons.map((c) => (
                  <div key={c.code} data-testid={`my-coupon-${c.code}`} className="flex flex-wrap items-center gap-3 bg-white border border-[#E5E1DA] rounded-lg px-4 py-3">
                    <Ticket size={18} className={c.status === "active" ? "text-[#b1976b]" : "text-gray-300"} />
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="font-bold tracking-widest text-[#1A1A1A]">{c.code}</code>
                        <span className="text-sm text-[#54595f]">₹{Number(c.amount_inr).toLocaleString("en-IN")} off</span>
                        <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_STYLES[c.status] || STATUS_STYLES.used}`}>
                          {c.status}
                        </Badge>
                      </div>
                      {c.expires_at && c.status === "active" && (
                        <p className="text-[11px] text-[#54595f] mt-0.5 flex items-center gap-1" data-testid={`my-coupon-expiry-${c.code}`}>
                          <CalendarClock size={11} /> Valid till {fmtDate(c.expires_at)}
                        </p>
                      )}
                      {c.status === "used" && c.used_on && (
                        <p className="text-[11px] text-[#54595f] mt-0.5" data-testid={`my-coupon-used-${c.code}`}>
                          Used on application <span className="font-semibold">{c.used_on}</span>{c.used_at ? ` · ${fmtDate(c.used_at)}` : ""} — you saved ₹{Number(c.amount_inr).toLocaleString("en-IN")}
                        </p>
                      )}
                    </div>
                    {c.status === "active" && (
                      <Button size="sm" variant="outline" className="ml-auto h-8 text-xs border-[#b1976b] text-[#b1976b]"
                        onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Coupon code copied"); }}>
                        <Copy size={12} className="mr-1" /> Copy
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <button type="button" data-testid="my-coupons-change-number-btn" onClick={reset}
              className="mt-4 text-[12px] font-semibold text-[#b1976b] underline">
              Check a different number
            </button>
          </>
        )}
      </div>
    </div>
  );
}
